import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, Card, Grid, IconButton, Button, Tooltip, 
  Chip, InputAdornment, Menu, MenuItem, ListItemIcon, CircularProgress,
  FormControl, InputLabel, Select, TextField, Avatar
} from '@mui/material';
import {
  Edit as EditIcon, 
  Visibility as ViewIcon, 
  Share as ShareIcon, 
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Archive as ArchiveIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import '../FormList/FormList.css';

const { confirm } = Modal;

const Tables = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTableForMenu, setSelectedTableForMenu] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  // formContent'i çoklu escape edilmiş olsa da parse etmeye çalışan yardımcı
  const safeParseFormContent = (input) => {
    try {
      if (!input) return null;
      let content = input;
      // İlk durumda doğrudan obje olabilir
      if (typeof content !== 'string') return content;
      // 3 denemeye kadar çift kaçışları çöz
      for (let i = 0; i < 3; i++) {
        try {
          // Baştan/sondan tırnak varsa ve içinde kaçışlı tırnaklar varsa önce bir kez JSON.parse
          if (typeof content === 'string') {
            const parsed = JSON.parse(content);
            if (typeof parsed === 'string') {
              content = parsed;
              continue;
            }
            return parsed;
          }
          return content;
        } catch (_) {
          // Son çare: tek adım normal parse deneyelim
          if (i === 2) {
            try { return JSON.parse(String(input)); } catch { return null; }
          }
          break;
        }
      }
      return null;
    } catch { return null; }
  };

  useEffect(() => {
    fetchTables();
  }, [userData, appToken]);

  useEffect(() => {
    // Filter tables based on search term and status filter
    let result = [...tables];
    
    if (searchTerm) {
      result = result.filter(table => 
        (table.title || 'İsimsiz Tablo').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(table => 
        statusFilter === 'active' ? table.isActive : !table.isActive
      );
    }
    
    // Sort tables
    result.sort((a, b) => {
      const dateA = new Date(a.createDate || 0);
      const dateB = new Date(b.createDate || 0);
      
      return sortOrder === 'newest' 
        ? dateB - dateA 
        : dateA - dateB;
    });
    
    setFilteredTables(result);
  }, [tables, searchTerm, statusFilter, sortOrder]);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://app.hhoms.com/api/form/Libraries/list/0/1", {
        headers: { ApplicationToken: appToken, User: userData.id }
      });
      
      // Sadece tablo tipindeki (fileType: 1) kayıtları filtrele
      const tableLibraries = (response.data.formLibraries || []).filter(lib => lib.fileType === 1);
      
      console.log('API Response:', response.data);
      console.log('Filtered tables:', tableLibraries);
      
      // Her tablo için formContent'i güvenli parse et ve sayfa sayısını hesapla
      const processedTables = tableLibraries.map(table => {
        console.log('Processing table:', table.id, 'formId:', table.formId, 'title:', table.title);
        let sheetCount = 0;
        let parsedContent = null;
        
        try {
          if (table.formContent) {
            parsedContent = safeParseFormContent(table.formContent);
            
            // Sayfa sayısını hesapla
            if (parsedContent && parsedContent.sheets && Array.isArray(parsedContent.sheets)) {
              sheetCount = parsedContent.sheets.length;
            }
          }
        } catch (error) {
          console.error(`Tablo ${table.id} formContent parse hatası:`, error);
          sheetCount = 0;
        }
        
        const processedTable = {
          ...table,
          sheetCount: sheetCount,
          parsedContent: parsedContent
        };
        
        console.log('Processed table:', processedTable.id, 'formId:', processedTable.formId);
        return processedTable;
      });
      
      setTables(processedTables);
      setFilteredTables(processedTables);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Tablolar yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTable = (tableId) => {
    navigate(`/spreadsheet-edit/${tableId}`);
  };

  const handleViewTable = (tableId) => {
    navigate(`/spreadsheet/${tableId}`);
  };

  const handleOpenTable = async (table) => {
    try {
      console.log('Opening table:', table);
      let targetFormId = table.formId;
      
      if (!targetFormId) {
        targetFormId = table.id;
        console.log('formId bulunamadı, table.id kullanılıyor:', targetFormId);
      }
      
      console.log('Target formId:', targetFormId);
      navigate(`/spreadsheet/${targetFormId}`);
    } catch (e) {
      console.error('Error opening table:', e);
      message.error('Tablo açılırken bir hata oluştu');
    }
  };

  const handleEditTableInNewTab = async (table) => {
    try {
      let targetFormId = table.formId;
      
      if (!targetFormId) {
        targetFormId = table.id;
        console.log('Edit için formId bulunamadı, table.id kullanılıyor:', targetFormId);
      }
      
      navigate(`/spreadsheet-edit/${targetFormId}`);
    } catch (e) {
      console.error('Error opening edit table:', e);
      message.error('Tablo düzenleme sayfası açılırken bir hata oluştu');
    }
  };

  const handleShare = (selectedTableForMenu) => {
    const shareUrl = `https://panel.formclicker.com/user-login?tableId=${selectedTableForMenu?.formId || selectedTableForMenu?.id}`;
    window.open(shareUrl, '_blank');
    message.success('Tablo paylaşım linkine yönlendirildiniz!');
    handleMenuClose();
  };

  // Toggle table status function
  const toggleTableStatus = async (tableId, currentStatus) => {
    try {
      await axios.put(
        `https://app.hhoms.com/api/form/Libraries/status/${tableId}/${!currentStatus}`,
        null,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setTables(prevTables => 
        prevTables.map(table => 
          table.id === tableId 
            ? { ...table, isActive: !currentStatus }
            : table
        )
      );
      
      message.success(`Tablo ${!currentStatus ? 'aktif' : 'pasif'} hale getirildi`);
    } catch (error) {
      message.error('Tablo durumu değiştirilirken bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD.MM.YYYY HH:mm');
  };

  const handleMenuClick = (event, table) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTableForMenu(table);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTableForMenu(null);
  };

  const handleCopyTable = async (table) => {
    const hide = message.loading('Tablo çoğaltılıyor...', 0);
    setCopyLoading(true);
    try {
      // Önce mevcut tablonun tüm detaylarını alalım
      const tableDetailResponse = await axios.get(
        `https://app.hhoms.com/api/form/Libraries/${table.formId}`,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id
          }
        }
      );

      if (!tableDetailResponse.data?.formLibraries?.[0]) {
        throw new Error('Tablo detayları alınamadı');
      }

      const originalTable = tableDetailResponse.data.formLibraries[0];
      
      // formContent string'ini parse edelim
      let parsedFormContent;
      try {
        parsedFormContent = typeof originalTable.formContent === 'string' 
          ? JSON.parse(originalTable.formContent)
          : originalTable.formContent;
      } catch (error) {
        console.error('Tablo içeriği parse edilemedi:', error);
        parsedFormContent = originalTable.formContent;
      }

      // 6 haneli rastgele bir formId oluştur
      const newFormId = Math.floor(100000 + Math.random() * 900000).toString();

      // Sheets alanlarını kopyala
      const sheets = parsedFormContent.sheets || [];
      
      // Settings alanlarını kopyala
      const settings = parsedFormContent.settings || {};

      // Yeni tablo için veriyi hazırlayalım
      const formContent = {
        id: newFormId,
        title: `${originalTable.title} (Kopya)`,
        description: parsedFormContent.description || '',
        sheets: sheets,
        settings: settings,
        isPublished: true,
        publishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formLink: ''
      };

      const tableData = {
        id: 0,
        title: `${originalTable.title} (Kopya)`,
        description: parsedFormContent.description || '',
        formId: newFormId,
        formContent: formContent, // obje olarak gönder
        isActive: originalTable.isActive,
        isDeleted: false,
        parentId: '0',
        fileType: 1 // Tablo tipi
      };

      // Yeni tabloyu oluşturalım
      const response = await axios.post(
        'https://app.hhoms.com/api/form/Libraries',
        tableData,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        hide();
        message.success('Tablo başarıyla çoğaltıldı');
        fetchTables();
      }
    } catch (error) {
      hide();
      message.error(`Tablo çoğaltılırken bir hata oluştu: ${error.message}`);
    } finally {
      setCopyLoading(false);
    }
  };

  const handleArchiveTable = async (tableId) => {
    confirm({
      title: 'Tabloyu Arşivlemek İstediğinizden Emin misiniz?',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: 'Tablo arşive taşınacak ve ana listeden kaldırılacaktır.',
      okText: 'Evet, Arşivle',
      cancelText: 'İptal',
      onOk: async () => {
        const hide = message.loading('Tablo arşivleniyor...', 0);
        setArchiveLoading(true);
        try {
          await axios.put(
            `https://app.hhoms.com/api/form/Libraries/archive/${tableId}/true`,
            null,
            {
              headers: {
                ApplicationToken: appToken,
                User: userData.id,
                'Content-Type': 'application/json'
              }
            }
          );
          hide();
          setTables(prevTables => prevTables.filter(table => table.id !== tableId));
          message.success('Tablo başarıyla arşivlendi');
        } catch (error) {
          hide();
          message.error('Tablo arşivlenirken bir hata oluştu');
        } finally {
          setArchiveLoading(false);
        }
      },
    });
  };

  return (
    <Box className="form-container">
      <Paper className="form-paper" elevation={2}>
        {/* Üst Başlık ve Arama/Filtreleme Araçları */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Kaydedilen Tablolar</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/spreadsheet')}>
              Yeni Tablo
            </Button>
          </Box>
        </Box>

        {/* Arama ve Filtreleme Araçları */}
        <Box className="search-filter-container" sx={{ mb: 3 }}>
          <Box className="search-box">
            <TextField
              size="small"
              placeholder="Tablo ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          </Box>
          
          <Box className="filter-sort-box">
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={statusFilter}
                label="Durum"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="passive">Pasif</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sırala</InputLabel>
              <Select
                value={sortOrder}
                label="Sırala"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="newest">En Yeni</MenuItem>
                <MenuItem value="oldest">En Eski</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Tablo Kartları */}
        <Grid container spacing={2}>
          {filteredTables.map((table) => (
            <Grid item xs={12} key={table.id}>
              <Card className="form-card">
                                 <Box className="card-header">
                   <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleOpenTable(table)}>
                     <Avatar 
                       className="header-avatar"
                       sx={{ 
                         bgcolor: table.isActive ? '#3B82F6' : '#94A3B8'
                       }}
                     >
                       <TableChartIcon sx={{ fontSize: 18, color: 'white' }} />
                     </Avatar>
                     <Tooltip title={table.title || 'İsimsiz Tablo'} placement="top">
                       <Typography className="form-title" noWrap sx={{ '&:hover': { color: '#3B82F6' } }}>
                         {table.title || 'İsimsiz Tablo'}
                       </Typography>
                     </Tooltip>
                   </Box>
                  <Box className="action-buttons">
                    <Chip 
                      className="status-chip"
                      label={table.isActive ? 'Aktif' : 'Pasif'}
                      size="small"
                      color={table.isActive ? 'success' : 'error'}
                      onClick={() => toggleTableStatus(table.id, table.isActive)}
                    />
                    <Chip 
                      className="response-chip"
                      label={`${table.sheetCount || 0} Sayfa`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Tooltip title="İşlemler">
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuClick(e, table)}
                        className="menu-button"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box className="card-content">
                  <Box className="date-info">
                    <Box className="date-item">
                      <CalendarIcon className="date-icon" />
                      <Typography variant="body2" className="date-text">
                        Oluşturulma: {formatDate(table.createDate)}
                      </Typography>
                    </Box>
                    <Box className="date-item">
                      <CalendarIcon className="date-icon" />
                      <Typography variant="body2" className="date-text">
                        Güncelleme: {formatDate(table.editDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ style: { minWidth: '150px' } }}
      >
        <MenuItem onClick={() => {
          handleViewTable(selectedTableForMenu?.formId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          Görüntüle
        </MenuItem>
        <MenuItem onClick={() => {
          handleEditTable(selectedTableForMenu?.formId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Düzenle
        </MenuItem>
        <MenuItem onClick={() => handleShare(selectedTableForMenu)}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          Paylaş
        </MenuItem>
        <MenuItem onClick={() => {
          handleCopyTable(selectedTableForMenu);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          Çoğalt
        </MenuItem>
        <MenuItem onClick={() => {
          handleArchiveTable(selectedTableForMenu?.id);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          Arşivle
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Tables;
